import React, { useState, useEffect, use } from "react";
import { FaInstagram, FaLinkedin, FaWhatsapp, FaAward, FaRocket, FaLightbulb, FaUserTie } from 'react-icons/fa';
import { motion } from 'framer-motion';
import bdrImg from './bdr-img.jfif';
import { useNavigate } from "react-router-dom";

const AboutFounder = () => {
  const navigate = useNavigate()
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const stats = [
    { icon: FaAward, value: "3+", label: "Années d'Expérience", color: "blue" },
    { icon: FaRocket, value: "25+", label: "Étudiants Formés", color: "emerald" },
    { icon: FaLightbulb, value: "15+", label: "Projets Innovants", color: "violet" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const floatAnimation = {
    initial: { y: 0 },
    animate: {
      y: [-5, 5, -5],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const gradientAnimation = {
    initial: { backgroundPosition: "0% 50%" },
    animate: {
      backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
      transition: {
        duration: 10,
        repeat: Infinity,
        ease: "linear"
      }
    }
  };

  return (
    <motion.section
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      variants={containerVariants}
      className="min-h-screen relative overflow-hidden bg-gray-50 to-white py-12 px-4"
    >
      {/* Animated Background Elements */}
      <motion.div
        animate={gradientAnimation}
        className="absolute inset-0 bg-[length:200%_200%] bg-gradient-to-br from-emerald-50/30 via-white to-violet-50/30 -z-10"
      />
      
      <div className="absolute top-10 left-10 w-72 h-72 bg-emerald-200/20 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-violet-200/20 rounded-full blur-3xl -z-10" />

      <div className="max-w-4xl mx-auto relative">
        {/* Profile Section with Enhanced Animation */}
        <motion.div
          variants={itemVariants}
          className="text-center mb-12"
        >
          <motion.div
            variants={floatAnimation}
            animate="animate"
            initial="initial"
            className="relative w-32 h-32 mx-auto mb-6"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ 
                type: "spring", 
                stiffness: 100, 
                damping: 15,
                delay: 0.2 
              }}
              className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full"
            />
            <motion.img
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ 
                type: "spring", 
                stiffness: 200, 
                damping: 15,
                delay: 0.3 
              }}
              src={bdrImg}
              alt="Badreddine Moussaoui"
              className="relative w-full h-full rounded-full object-cover border-4 border-white shadow-2xl"
            />
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ 
                type: "spring", 
                delay: 0.5 
              }}
              className="absolute -bottom-2 -right-2 w-12 h-12 bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-full flex items-center justify-center shadow-lg"
            >
              <FaUserTie className="text-white text-lg" />
            </motion.div>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-4xl font-bold text-gray-900 mb-2"
          >
            Badreddine <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-emerald-800">Moussaoui</span>
          </motion.h1>
          
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "100px" }}
            transition={{ delay: 0.5, duration: 0.8, ease: "easeOut" }}
            className="h-1 bg-gradient-to-r from-transparent via-emerald-600 to-transparent mx-auto mb-4"
          />
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-gray-600 text-lg font-medium"
          >
           Architect & Designer
          </motion.p>
        </motion.div>
        {/* Content Card */}
        <motion.div
          variants={itemVariants}
          whileHover={{ 
            y: -5,
            boxShadow: "0 20px 40px rgba(5, 150, 105, 0.1)"
          }}
          className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-emerald-100/50"
        >
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="flex items-center gap-4 mb-6"
          >
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
              className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl"
            >
              <FaLightbulb className="text-2xl text-white" />
            </motion.div>
            <h2 className="text-2xl font-bold text-gray-900">Vision & Mission</h2>
          </motion.div>
          
         <motion.p
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
  transition={{ delay: 1 }}
  className="text-gray-700 text-lg mb-6 leading-relaxed"
>
  Passionné par l'architecture moderne et l'innovation pédagogique, je démocratise l'accès à une formation d'excellence en architecture et design.
</motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.1 }}
            whileHover={{ scale: 1.01 }}
            className="bg-gradient-to-r from-emerald-50 to-emerald-100/50 rounded-2xl p-6 border-l-4 border-emerald-600 mb-8"
          >
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="text-gray-800 text-lg font-semibold italic"
            >
              "Former la prochaine génération de créateurs avec une approche pratique et créative."
            </motion.p>
          </motion.div>
          
         <div className="flex flex-wrap items-center gap-4">
  <motion.button
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 1.2 }}
    onClick={() => navigate("/portfolio-projects")}
    className="
      inline-flex items-center gap-2
      px-6 py-3
      text-sm font-semibold
      text-white
      bg-emerald-600
      rounded-full
      hover:bg-emerald-700
      transition-all
      shadow-md hover:shadow-lg
    "
  >
    Check Portfolio
  </motion.button>
  
  <motion.a
    whileHover={{ scale: 1.1, y: -3 }}
    whileTap={{ scale: 0.95 }}
    href="https://www.instagram.com/bdroaa/?hl=fr"
    target="_blank"
    rel="noopener noreferrer"
    className="p-3 bg-pink-50 text-pink-600 rounded-lg hover:bg-pink-100 transition-colors duration-300"
  >
    <FaInstagram className="text-xl" />
  </motion.a>
  
  <motion.a
    whileHover={{ scale: 1.1, y: -3 }}
    whileTap={{ scale: 0.95 }}
    href="https://www.linkedin.com/in/moussaoui-badreddine-692836250"
    target="_blank"
    rel="noopener noreferrer"
    className="p-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors duration-300"
  >
    <FaLinkedin className="text-xl" />
  </motion.a>
</div>
        </motion.div>

      </div>
    </motion.section>
  );
};

export default AboutFounder;


{/* <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="text-base font-semibold text-gray-900 mb-4 text-center">Contact & Réseaux</h3>
              <div className="flex justify-center gap-6">
                <motion.a
                  whileHover={{ scale: 1.1, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  href="https://www.instagram.com/ctrl.plusx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-pink-50 text-pink-600 rounded-lg hover:bg-pink-100 transition-colors duration-300"
                >
                  <FaInstagram className="text-xl" />
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.1, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  href="https://www.linkedin.com/in/moussaoui-badreddine-692836250"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors duration-300"
                >
                  <FaLinkedin className="text-xl" />
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.1, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  href="https://wa.me/213549075577"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-colors duration-300"
                >
                  <FaWhatsapp className="text-xl" />
                </motion.a>
              </div>
              <p className="text-xs text-gray-500 text-center mt-4">
                Cliquez pour me contacter directement
              </p>
            </div> */}